/**
 * Isolated route shell for Organic Content.
 * Keeps draft UI failures inside this tab (error boundary + suspense).
 * Outer wrapper owns h-full + bg-white so nav switches never flash a black frame.
 */

import React, { Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary, lazyWithRetry } from '@tracekit/core';
import { ORGANIC_GENERATE_ENABLED } from './organicContentModel';

const OrganicContentPage = lazyWithRetry(() => import('./OrganicContentPage'));

export function OrganicContentFallback() {
  return (
    <div className="flex h-full items-center justify-center gap-2 bg-white text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading Organic Content…
    </div>
  );
}

export function OrganicContentErrorFallback() {
  return (
    <div className="flex h-full flex-col items-center justify-center bg-white p-8 text-center">
      <h2 className="text-lg font-semibold text-slate-800">
        Organic Content is temporarily unavailable
      </h2>
      <p className="mt-2 max-w-md text-sm text-slate-600">
        This desk could not be loaded. Other GovEarner tabs continue to work. Try again in a
        moment.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-4 min-h-11 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
      >
        Reload tab
      </button>
    </div>
  );
}

export function OrganicContentRoute() {
  return (
    <div
      className="h-full min-h-0 w-full bg-white"
      data-testid="organic-content-route"
      data-organic-generate={ORGANIC_GENERATE_ENABLED ? 'true' : 'false'}
    >
      <ErrorBoundary fallback={<OrganicContentErrorFallback />}>
        <Suspense fallback={<OrganicContentFallback />}>
          <OrganicContentPage />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

export default OrganicContentRoute;
